import React, { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Result,
  Table,
  Alert,
  Space,
  Typography,
  Progress,
} from 'antd';
import {
  InboxOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ImportModalProps, ImportError, ImportResult } from '../types';
import { DesignTokens } from '../DesignTokens';

const { Dragger } = Upload;
const { Title, Text, Link } = Typography;

type StepType = 'upload' | 'importing' | 'result';

export const ImportModal: React.FC<ImportModalProps> = ({
  open,
  onCancel,
  onImport,
}) => {
  const [currentStep, setCurrentStep] = useState<StepType>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleDownloadTemplate = () => {
    // 下载Excel模板
    const link = document.createElement('a');
    link.href = '/templates/user_import_template.xlsx';
    link.download = '用户导入模板.xlsx';
    link.click();
  };

  const uploadProps: UploadProps = {
    accept: '.xlsx,.xls,.csv',
    multiple: false,
    maxCount: 1,
    beforeUpload: (file) => {
      setFile(file);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFile(null);
    },
  };

  const handleImport = async () => {
    if (!file || !onImport) return;

    setImporting(true);
    setCurrentStep('importing');
    setProgress(0);

    // 模拟进度
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 10;
      });
    }, 200);

    try {
      const importResult = await onImport(file);
      clearInterval(progressInterval);
      setProgress(100);
      setResult(importResult);
      setCurrentStep('result');
    } catch (error) {
      clearInterval(progressInterval);
      console.error('导入失败:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep('upload');
    setFile(null);
    setProgress(0);
    setResult(null);
    onCancel?.();
  };

  const errorColumns: ColumnsType<ImportError> = [
    {
      title: '行号',
      dataIndex: 'row',
      key: 'row',
      width: 80,
      align: 'center',
    },
    {
      title: '字段',
      dataIndex: 'field',
      key: 'field',
      width: 120,
    },
    {
      title: '值',
      dataIndex: 'value',
      key: 'value',
      ellipsis: true,
    },
    {
      title: '错误信息',
      dataIndex: 'error',
      key: 'error',
      render: (error: string) => (
        <Text type="danger">{error}</Text>
      ),
    },
  ];

  const renderContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="导入须知"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>请使用系统提供的模板文件</li>
                  <li>单次导入不超过1000条数据</li>
                  <li>用户名、真实姓名、手机号为必填项</li>
                  <li>手机号不能重复</li>
                </ul>
              }
              type="info"
              showIcon
            />

            <div style={{ textAlign: 'center' }}>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                下载导入模板
              </Button>
            </div>

            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: DesignTokens.colors.primary }} />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持 .xlsx、.xls、.csv 格式文件
              </p>
            </Dragger>

            {file && (
              <Alert
                message={`已选择文件: ${file.name}`}
                type="success"
                showIcon
              />
            )}
          </Space>
        );

      case 'importing':
        return (
          <Space
            direction="vertical"
            size="large"
            style={{ width: '100%', padding: '40px 0' }}
          >
            <div style={{ textAlign: 'center' }}>
              <Title level={4}>正在导入数据...</Title>
              <Progress
                type="circle"
                percent={progress}
                strokeColor={DesignTokens.colors.primary}
                style={{ marginTop: 20 }}
              />
            </div>
          </Space>
        );

      case 'result':
        if (!result) return null;

        const hasErrors = result.failed > 0;

        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Result
              status={hasErrors ? 'warning' : 'success'}
              title={hasErrors ? '导入完成（部分失败）' : '导入成功'}
              subTitle={
                <Space direction="vertical" size="small">
                  <Text>成功导入 {result.success} 条数据</Text>
                  {hasErrors && (
                    <Text type="danger">失败 {result.failed} 条数据</Text>
                  )}
                </Space>
              }
              icon={
                hasErrors ? (
                  <CloseCircleOutlined style={{ color: DesignTokens.colors.warning }} />
                ) : (
                  <CheckCircleOutlined style={{ color: DesignTokens.colors.success }} />
                )
              }
            />

            {hasErrors && result.errors && result.errors.length > 0 && (
              <div>
                <Title level={5}>错误明细</Title>
                <Table
                  columns={errorColumns}
                  dataSource={result.errors}
                  rowKey={(record) => `${record.row}-${record.field}`}
                  pagination={{ pageSize: 5 }}
                  size="small"
                />
              </div>
            )}
          </Space>
        );

      default:
        return null;
    }
  };

  const getFooter = () => {
    switch (currentStep) {
      case 'upload':
        return [
          <Button key="cancel" onClick={handleClose}>
            取消
          </Button>,
          <Button
            key="import"
            type="primary"
            disabled={!file}
            onClick={handleImport}
          >
            开始导入
          </Button>,
        ];

      case 'importing':
        return null;

      case 'result':
        return [
          <Button key="close" type="primary" onClick={handleClose}>
            关闭
          </Button>,
        ];

      default:
        return null;
    }
  };

  return (
    <Modal
      title="批量导入用户"
      open={open}
      onCancel={handleClose}
      footer={getFooter()}
      width={720}
      closable={!importing}
      maskClosable={!importing}
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
      }}
    >
      <Steps
        current={
          currentStep === 'upload' ? 0 : currentStep === 'importing' ? 1 : 2
        }
        items={[
          { title: '上传文件' },
          { title: '导入数据' },
          { title: '完成' },
        ]}
        style={{ marginBottom: DesignTokens.spacing.xl }}
      />

      {renderContent()}
    </Modal>
  );
};
